import React, { useEffect, useRef, useState } from "react";
import * as linesOrdersTypes from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import Order, { canIgnoreEarliestStart } from "./Order";
import ShiftEditorModal from "@/modules/pg/shared/planningTable/modals/ShiftEditorModal";
import ShiftEditorWeek from "@/modules/shared/planningTable/ShiftEditorWeek";
import { connect, useSelector } from "react-redux";
import type { Dispatch } from "redux";
import {
    DndContext,
    DragEndEvent,
    DragStartEvent,
    PointerSensor,
    pointerWithin,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Dropdown } from "react-bootstrap";
import { DragOverlayItem, SortableTableBody, type SortableDragHandleProps } from "@/modules/shared/ui/DraggableList";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { highlightRescheduleLine } from "@/lib/ganttChartUtils";
import { UPDATE_SHIFTS_ACTION_NAME } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import { SET_RESCHEDULE_LINE_UUID } from "@/modules/pg/shared/planningTable/reducers/common";
import { RESET_FILTERS } from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import { shiftNumber, dateFromWeekAndShift, splitIntoArrOfArr, deepClone, isNumber, classNames } from "@/lib/util";
import {
    LinesOrdersLogic,
    PropertiesLogic,
    ReduxFilters,
} from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import { PLANNING_EXPLANATION_TYPE } from "@/lib/ManufacturingConsts.generated";
import { TypeFilterValues } from "@/modules/shared/planningTable/typeFilterConsts";
import { getOrgData } from "@/lib/auth";
import { ORDER_TAGS_ACCESS, ORDER_TAGS_SETTER } from "@/lib/ManufacturingTags.generated";

import type { ReportLoadingAction } from "@/modules/pg/shared/planningTable/reducers/report";
import type { SetRescheduleOrderBundleAction } from "@/modules/pg/shared/planningTable/reducers/common";
import type { SetHighlightedOrders } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { ResetValidLinesAction } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type {
    IOrderProducedModelEx,
    MoveLineOrderAction,
    UpdateLineOrderAction,
    UpdateLineOrdersAction,
    UpdateShiftsAction,
} from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import type {
    SetRescheduleLineAction,
    SetRescheduleOrderAction,
} from "@/modules/pg/shared/planningTable/reducers/common";
import type {
    ResetFiltersAction,
    SetLastClickedOrderUuid,
} from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type { ITags } from "@/lib/models";
import { SHIFTS_IN_WEEK } from "@/modules/pg/shared/planningTable/ganttChart/constants";
import { GanttChartSource } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import { REPORT_LOADING_ACTION_NAME } from "@/modules/pg/shared/planningTable/reducers/report";
import type { PlanningTableColumn } from "@/modules/pg/shared/viewsCommon";

type ShiftsForLineActionTypes = UpdateShiftsAction;
type ShiftsForLineOwnProps = {
    line_uuid: string;
    line_title: string;
    line_group_uuid: string;
    week: number;
    year: number;
};
type ShiftsForLineProps = ShiftsForLineOwnProps & ShiftsForLineReduxProps;

type ShiftsForLineState = {
    show_shift_editor: boolean;
    shifts: t.IShiftLineRec[];
    shifts_split: t.IShiftLineRec[][][];
};

class ShiftsForLine extends React.Component<ShiftsForLineProps, ShiftsForLineState> {
    state: ShiftsForLineState = {
        show_shift_editor: false,
        shifts_split: [],
        shifts: [],
    };

    componentDidMount() {
        const shifts = deepClone(this.props.shifts);
        this.setState({ shifts }, () => this.reloadShifts());
    }

    componentDidUpdate(prev_props: ShiftsForLineProps) {
        if (prev_props.shifts !== this.props.shifts) {
            const shifts = deepClone(this.props.shifts);
            this.setState({ shifts }, () => this.reloadShifts());
        }
    }

    getShiftEditWeek = () => {
        return (
            PropertiesLogic.getNumShiftsFromDayTag(GanttChartSource.planning_table, this.props.line_group_uuid) /
            SHIFTS_IN_WEEK
        );
    };

    reloadShifts = () => {
        const shift_edit_weeks = this.getShiftEditWeek();
        const shifts_split: t.IShiftLineRec[][][] = [];

        for (let shift_editor_i = 0; shift_editor_i < shift_edit_weeks; shift_editor_i++) {
            const { year: future_year, week } = this.getWeekAndYear(shift_editor_i);
            const shift_week = this.state.shifts.filter(x => x.week === week && x.year === future_year);
            shifts_split[week] = splitIntoArrOfArr(shift_week, 3);
        }

        this.setState({ shifts_split });
    };

    openModal = () => {
        this.setState({ show_shift_editor: true });
    };

    getWeekAndYear = (offset: number, week: number | null = null) => {
        const shift = shiftNumber(dateFromWeekAndShift((week || this.props.week) + offset, this.props.year, 0));
        return { week: shift.week, year: shift.year };
    };

    handleShiftClick = (shift: number, day: number, active: boolean, week: number, year: number) => {
        const index = 3 * day + shift;

        const shifts = this.state.shifts.filter(s => s.week === week && s.year === year);
        const new_shift = shifts[index];
        const original_index = this.state.shifts.findIndex(s => s === new_shift);
        new_shift.enabled = !active;

        const state_shifts = this.state.shifts;
        state_shifts[original_index] = new_shift;

        this.setState({ shifts: [...this.state.shifts] }, () => this.reloadShifts());
    };

    toggleAllShifts = (day_shift: number, week: number, year: number) => {
        // Set new value for `enabled` based on the current state
        const should_enable = this.state.shifts.some(
            s => s.week === week && s.year === year && s.shift_number % 3 === day_shift && !s.enabled
        );

        // Set `enabled` on all matching shifts
        const new_shifts = this.state.shifts.map(shift =>
            shift.week === week && shift.year === year && shift.shift_number % 3 === day_shift
                ? { ...shift, enabled: should_enable }
                : shift
        );
        this.setState({ shifts: new_shifts }, this.reloadShifts);
    };

    getShiftEditors = () => {
        const shiftEditors: React.ReactNode[] = [];
        const shift_edit_weeks = this.getShiftEditWeek();
        for (let shift_editor_i = 0; shift_editor_i < shift_edit_weeks; shift_editor_i++) {
            const { week } = this.getWeekAndYear(shift_editor_i);
            shiftEditors.push(
                <ShiftEditorWeek
                    current_shift={-1}
                    current_week={week}
                    key={this.props.line_uuid + "_shifts_" + shift_editor_i}
                    shifts={this.state.shifts_split[week]}
                    show_overrides={true}
                    show_dates={true}
                    handleClick={(shift, day, active, week_arg, year) =>
                        this.handleShiftClick(shift, day, active, week, year)
                    }
                    toggleAllShifts={(shift, week, year) => this.toggleAllShifts(shift, week, year)}
                />
            );
        }

        return shiftEditors;
    };

    onCloseModal = (e?: React.MouseEvent<HTMLElement>) => {
        const shifts = deepClone(this.props.shifts);
        this.setState({ shifts, show_shift_editor: false }, () => this.reloadShifts());

        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    onSaveModal = (e?: React.MouseEvent<HTMLElement>) => {
        this.setState({ show_shift_editor: false });

        if (this.state.shifts !== this.props.shifts) {
            this.props.reduxDispatch({
                type: UPDATE_SHIFTS_ACTION_NAME,
                data: {
                    line_uuid: this.props.line_uuid,
                    shifts: this.state.shifts,
                },
            });
        }

        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    render() {
        const shift_editors = this.getShiftEditors();

        return (
            <button className="dropdown-item" onClick={this.openModal}>
                {translate("common.shifts", "shifts")}
                <ShiftEditorModal
                    onClose={this.onCloseModal}
                    onSave={this.onSaveModal}
                    line_title={this.props.line_title}
                    show={this.state.show_shift_editor}
                >
                    {shift_editors}
                </ShiftEditorModal>
            </button>
        );
    }
}

const shiftsMapState = (state: ReduxState, own_props: ShiftsForLineOwnProps) => {
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const line_orders = lines_orders.find(lo => lo.line_uuid === own_props.line_uuid);

    let shifts: t.IShiftLineRec[] = [];

    if (line_orders) {
        shifts = line_orders.shifts;
    }

    return { shifts };
};

const shiftsMapDispatch = (dispatch: Dispatch<ShiftsForLineActionTypes>) => ({
    reduxDispatch: dispatch,
});
type ShiftsForLineReduxProps = ReturnType<typeof shiftsMapState> & ReturnType<typeof shiftsMapDispatch>;
const ConnectedShiftsForLine = connect<
    ReturnType<typeof shiftsMapState>,
    ReturnType<typeof shiftsMapDispatch>,
    ShiftsForLineOwnProps,
    ReduxState
>(
    shiftsMapState,
    shiftsMapDispatch
)(ShiftsForLine);

// RescheduleLine

type RescheduleLineActionTypes =
    | SetRescheduleLineAction
    | ResetValidLinesAction
    | SetHighlightedOrders
    | ResetFiltersAction;
type RescheduleLineOwnProps = { line_uuid: string };
type RescheduleLineProps = RescheduleLineOwnProps & RescheduleLineReduxProps;

type RescheduleLineState = {};

class RescheduleLine extends React.Component<RescheduleLineProps, RescheduleLineState> {
    componentDidUpdate(prev_props: RescheduleLineProps) {
        // unhighlight lines
        if (
            prev_props.is_enabled_reschedule !== this.props.is_enabled_reschedule &&
            this.props.is_enabled_reschedule === false
        ) {
            highlightRescheduleLine(null);
        }
    }

    rescheduleLine = async () => {
        if (this.props.is_enabled_reschedule) {
            this.props.reduxDispatch({ type: RESET_FILTERS, data: undefined });
        } else {
            this.props.reduxDispatch({ type: SET_RESCHEDULE_LINE_UUID, data: this.props.line_uuid });
        }
    };

    render() {
        return (
            <button className="dropdown-item" onClick={this.rescheduleLine}>
                {this.props.is_enabled_reschedule
                    ? translate("common.cancel", "Cancel")
                    : translate("common.reschedule", "Reschedule")}
            </button>
        );
    }
}

const rescheduleLineMapState = (state: ReduxState, own_props: RescheduleLineOwnProps) => {
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const filters = state.gantt_chart_filters;
    const is_enabled_reschedule =
        filters.selected_filter_type === TypeFilterValues.reschedule_line &&
        filters.reschedule_line_uuid === own_props.line_uuid;
    return {
        lines_orders,
        is_enabled_reschedule,
        original_report_created_at: state.gantt_chart_report.original_report_created_at,
    };
};
const rescheduleLineMapDispatch = (dispatch: Dispatch<RescheduleLineActionTypes>) => ({
    reduxDispatch: dispatch,
});
type RescheduleLineReduxProps = ReturnType<typeof rescheduleLineMapState> &
    ReturnType<typeof rescheduleLineMapDispatch>;
const ConnectedRescheduleLine = connect<
    ReturnType<typeof rescheduleLineMapState>,
    ReturnType<typeof rescheduleLineMapDispatch>,
    RescheduleLineOwnProps,
    ReduxState
>(
    rescheduleLineMapState,
    rescheduleLineMapDispatch
)(RescheduleLine);

type PlanningSortableOrderBodyProps = {
    id: string;
    line_uuid: string;
    enabled_edit?: boolean;
    children: (dragHandleProps: SortableDragHandleProps) => React.ReactNode;
};

/** Applies planning-table readonly rules, then delegates to shared SortableTableBody. */
const PlanningSortableOrderBody = ({ id, line_uuid, enabled_edit, children }: PlanningSortableOrderBodyProps) => {
    const { is_readonly, is_selected, report_loading } = useSelector((state: ReduxState) => {
        const report_loading = state.gantt_chart_report.report_loading;
        const line = state.gantt_chart_lines_orders.lines_orders.find(lo => lo.line_uuid === line_uuid);
        const order = line?.orders.find(o => o.uuid === id);
        const is_selected = state.gantt_chart_planning_table.last_clicked_order_uuid === id;

        if (!line || !order) {
            return { is_readonly: true, is_selected, report_loading };
        }

        return { is_readonly: LinesOrdersLogic.isOrderReadOnly(line, order), is_selected, report_loading };
    });

    return (
        <SortableTableBody
            id={id}
            disabled={enabled_edit === false || is_readonly || report_loading}
            className={classNames("order-group-body", { "order-group-body--selected": is_selected })}
        >
            {children}
        </SortableTableBody>
    );
};

type RenderOrderGroupProps = {
    value: IOrderProducedModelEx;
    sortIndex: number;
    line_uuid: string;
    enabled_edit?: boolean;
    plant_uuid: string;
    draw_split_line: boolean;
    columns: PlanningTableColumn[];
    dragHandleProps?: SortableDragHandleProps | null;
};

const renderOrderGroup = ({
    value,
    sortIndex,
    line_uuid,
    enabled_edit,
    plant_uuid,
    draw_split_line,
    columns,
    dragHandleProps,
}: RenderOrderGroupProps) => {
    return (
        <Order
            order_uuid={value.uuid}
            sortIndex={sortIndex}
            line_uuid={line_uuid}
            enabled_edit={enabled_edit}
            plant_uuid={plant_uuid}
            columns={columns}
            campaign_group={value.campaign_group}
            dragHandleProps={dragHandleProps}
            draw_split_line={draw_split_line}
        />
    );
};

type SortableListProps = {
    plant_uuid: string;
    line_uuid: string;
    columns: PlanningTableColumn[];
    items: IOrderProducedModelEx[];
    enabled_edit: boolean | undefined;
    recommend_split_order_uuid: string;
    onSortEnd: (args: { activeId: string; overId: string }) => boolean;
};

///////////////////////////////////////////////////
// Production plan order list
export const SortableList = ({
    plant_uuid,
    line_uuid,
    columns,
    items: itemsFromProps,
    enabled_edit,
    recommend_split_order_uuid,
    onSortEnd,
}: SortableListProps) => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [items, setItems] = useState<IOrderProducedModelEx[]>(itemsFromProps);
    const skipNextSyncRef = useRef(false);
    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        if (activeId !== null) {
            return;
        }
        if (skipNextSyncRef.current) {
            skipNextSyncRef.current = false;
            return;
        }
        setItems(itemsFromProps);
    }, [itemsFromProps, activeId]);

    const itemIds = items.map(item => item.uuid);
    const activeItem = activeId ? items.find(item => item.uuid === activeId) : undefined;
    const activeIndex = activeItem ? items.indexOf(activeItem) : -1;

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(String(event.active.id));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            setActiveId(null);
            return;
        }

        const activeUuid = String(active.id);
        const overUuid = String(over.id);
        const oldIndex = items.findIndex(item => item.uuid === activeUuid);
        const newIndex = items.findIndex(item => item.uuid === overUuid);
        if (oldIndex >= 0 && newIndex >= 0) {
            const accepted = onSortEnd({ activeId: activeUuid, overId: overUuid });
            if (accepted) {
                skipNextSyncRef.current = true;
                setItems(arrayMove(items, oldIndex, newIndex));
            }
        }
        setActiveId(null);
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <table className="planning-table__sortable-list" data-testid="planning-table-line">
                <thead>
                    <tr>
                        {columns.map(column => {
                            const style: React.CSSProperties = {
                                minWidth: column.min_width || "auto",
                                // Trick to make the ellipsis work with variable widths
                                maxWidth: column.min_width || "auto",
                                textAlign: column.align || "auto",
                            };

                            return (
                                <th
                                    key={column.id}
                                    title={column.value}
                                    className={`sortable-element__${column.id}`}
                                    style={style}
                                >
                                    {column.value}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                    {items.map((value, index) => (
                        <PlanningSortableOrderBody
                            key={value.uuid}
                            id={value.uuid}
                            line_uuid={line_uuid}
                            enabled_edit={enabled_edit}
                        >
                            {dragHandleProps =>
                                renderOrderGroup({
                                    value,
                                    sortIndex: index,
                                    line_uuid,
                                    enabled_edit,
                                    plant_uuid,
                                    draw_split_line:
                                        recommend_split_order_uuid !== "" && recommend_split_order_uuid === value.uuid,
                                    columns,
                                    dragHandleProps,
                                })
                            }
                        </PlanningSortableOrderBody>
                    ))}
                </SortableContext>
            </table>
            {activeItem && activeIndex >= 0 && (
                <DragOverlayItem>
                    <table className="planning-table__sortable-list">
                        <tbody className="order-group-body sortable-element__dragging">
                            {renderOrderGroup({
                                value: activeItem,
                                sortIndex: activeIndex,
                                line_uuid,
                                enabled_edit,
                                plant_uuid,
                                draw_split_line:
                                    recommend_split_order_uuid !== "" && recommend_split_order_uuid === activeItem.uuid,
                                columns,
                                // Visual-only handles in overlay (drag already active)
                                dragHandleProps: {},
                            })}
                        </tbody>
                    </table>
                </DragOverlayItem>
            )}
        </DndContext>
    );
};

///////////////////////////////////////////////////
// Production plan order table widget
type LineOrderListActionTypes =
    | SetHighlightedOrders
    | UpdateLineOrderAction
    | UpdateLineOrdersAction
    | MoveLineOrderAction
    | SetRescheduleOrderAction
    | ResetFiltersAction
    | SetLastClickedOrderUuid
    | SetRescheduleOrderBundleAction
    | ReportLoadingAction;

type LineOrderListOwnProps = {
    line_uuid: string;
    line_group_uuid: string;
    week: number;
    year: number;
    plant_uuid: string;
    columns: PlanningTableColumn[];
    onClickInfo: (line_title: string) => (e: React.MouseEvent<HTMLElement>) => void;
    enabled_edit?: boolean;
    compact?: boolean;
};

type Props = LineOrderListOwnProps & ReduxProps;

type State = {
    focused: boolean;
    is_list_visible: boolean;
};

class LineOrderList extends React.Component<Props, State> {
    state: State = {
        focused: false,
        is_list_visible: true,
    };

    getOrders = (): IOrderProducedModelEx[] => {
        const { line_orders } = this.props;
        return (line_orders && line_orders.orders) || [];
    };

    getListedOrders = (): IOrderProducedModelEx[] => {
        const orders = this.getOrders();

        // HACK: In general unfiltered orders are shown as translucent. At the moment we completely
        // hide them only for Agrana.
        if (getOrgData().code == "agrana") {
            return orders.filter(order => this.props.filtered_order_uuids.has(order.uuid));
        }

        return orders;
    };

    onSortEnd = ({ activeId, overId }: { activeId: string; overId: string }): boolean => {
        const orders = this.getOrders();
        const oldIndex = orders.findIndex(order => order.uuid === activeId);
        const newIndex = orders.findIndex(order => order.uuid === overId);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
            return false;
        }

        void this.applySortMove(activeId, oldIndex, newIndex);
        return true;
    };

    applySortMove = async (activeId: string, oldIndex: number, newIndex: number) => {
        const order_sort = true;

        await ReduxFilters.setRescheduleOrderUuid(
            this.props.reduxDispatch,
            activeId,
            "line_order_list",
            this.props.is_filter_locked,
            order_sort
        );

        this.props.reduxDispatch({
            type: REPORT_LOADING_ACTION_NAME,
            data: true,
        });

        this.props.reduxDispatch({
            type: linesOrdersTypes.MOVE_LINE_ORDER_ACTION_NAME,
            data: {
                line_uuid: this.props.line_uuid,
                old_index: oldIndex,
                new_index: newIndex,
            },
        });
    };

    onMakeAll247 = () => {
        const { line_orders } = this.props;
        if (!line_orders) {
            return;
        }

        const orders = this.getOrders();
        for (const order of orders) {
            order.sim_ignore_earliest_start = true;
        }

        this.props.reduxDispatch({
            type: linesOrdersTypes.UPDATE_LINE_ORDERS_ACTION_NAME,
            data: {
                ...line_orders,
                orders,
            },
        });
    };

    onFreezeWholeLine = (do_freeze: boolean) => {
        const { line_orders } = this.props;
        if (!line_orders) {
            return;
        }

        const orders = this.getOrders();
        const new_orders: IOrderProducedModelEx[] = [];
        for (const order of orders) {
            if (ORDER_TAGS_ACCESS.insight_freeze_order(order.tags) === do_freeze) {
                new_orders.push(order);
                continue;
            }

            const new_order: IOrderProducedModelEx = { ...order };
            const new_tags: ITags = { ...new_order.tags };
            ORDER_TAGS_SETTER.insight_freeze_order(new_tags, do_freeze);

            if (new_order.time_start !== undefined && isNumber(new_order.time_start)) {
                ORDER_TAGS_SETTER.freeze_time_start(new_tags, new_order.time_start);
            }
            if (new_order.time_end !== undefined && isNumber(new_order.time_end)) {
                ORDER_TAGS_SETTER.freeze_time_end(new_tags, new_order.time_end);
            }

            new_order.tags = new_tags;
            new_orders.push(new_order);
        }

        this.props.reduxDispatch({
            type: linesOrdersTypes.UPDATE_LINE_ORDERS_ACTION_NAME,
            data: {
                ...line_orders,
                orders: new_orders,
            },
        });
    };

    handleHeaderFocus = () => {
        this.setState({ focused: true });
    };

    handleHeaderBlur = () => {
        this.setState({ focused: false });
    };

    handleTitleClick = () => {
        this.setState({ is_list_visible: !this.state.is_list_visible });
    };

    /////////////////////////////////////////
    // Render component
    render() {
        if (!this.props.show_line) {
            return null;
        }

        const { line_orders, columns } = this.props;
        const has_orders = this.getOrders().length > 0;
        const listed_orders = this.getListedOrders();
        const line_title = line_orders?.line_title ?? "";
        const line_tags: ITags = line_orders?.tags ?? {};
        const show_24_7 = canIgnoreEarliestStart(line_tags);
        return (
            <div className="planning-table__row" style={{ caretColor: "transparent" }}>
                <div
                    onClick={this.handleTitleClick}
                    className="planning-table__row-title pt-2 mt-n2"
                    style={{
                        zIndex: this.state.focused ? 2 : 1,
                        position: "sticky",
                        top: "0px",
                        backgroundColor: "white",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>
                            <span className="info-icon mr-2" onClick={this.props.onClickInfo(line_title)}>
                                <i className="fas fa-info-circle"></i>
                            </span>
                            {line_title}
                        </span>
                        <div>
                            <Dropdown
                                onClick={event => event.stopPropagation()}
                                onFocus={this.handleHeaderFocus}
                                onBlur={this.handleHeaderBlur}
                            >
                                <Dropdown.Toggle as="button" className="btn btn-short btn-flat-light px-1 py-0">
                                    <i className="fas fa-ellipsis-h" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="right" style={{ opacity: this.state.focused ? 1 : 0 }}>
                                    <ConnectedShiftsForLine
                                        line_uuid={this.props.line_uuid}
                                        line_title={line_title}
                                        line_group_uuid={this.props.line_group_uuid}
                                        week={this.props.week}
                                        year={this.props.year}
                                    />
                                    <ConnectedRescheduleLine line_uuid={this.props.line_uuid} />
                                    {show_24_7 && (
                                        <button
                                            className="dropdown-item"
                                            disabled={!has_orders}
                                            onClick={this.onMakeAll247}
                                        >
                                            {translate("Manufacturing.Planning.make_all_24_7", "Make all 24/7")}
                                        </button>
                                    )}
                                    <button
                                        className="dropdown-item"
                                        disabled={!has_orders}
                                        onClick={() => this.onFreezeWholeLine(true)}
                                    >
                                        {translate("Manufacturing.Planning.freeze_whole_line", "Freeze whole line")}
                                    </button>
                                    <button
                                        className="dropdown-item"
                                        disabled={!has_orders}
                                        onClick={() => this.onFreezeWholeLine(false)}
                                    >
                                        {translate(
                                            "Manufacturing.Planning.unfreeze_whole_line",
                                            "Un-freeze whole line"
                                        )}
                                    </button>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                {this.state.is_list_visible && (
                    <div>
                        {listed_orders.length === 0 ? (
                            <div className="planning-table__no-orders">
                                {translate("Manufacturing.Planning.no_orders", "No orders found.")}
                            </div>
                        ) : (
                            <SortableList
                                plant_uuid={this.props.plant_uuid}
                                line_uuid={this.props.line_uuid}
                                columns={columns}
                                items={listed_orders}
                                enabled_edit={this.props.enabled_edit}
                                recommend_split_order_uuid={this.props.recommend_split_order_uuid}
                                onSortEnd={this.onSortEnd}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }
}

const lineOrderListMapState = (state: ReduxState, ownProps: LineOrderListOwnProps) => {
    const line_orders =
        state.gantt_chart_lines_orders.lines_orders.find(lo => lo.line_uuid === ownProps.line_uuid) ?? null;

    // check if this lines passed the filter
    const valid_lines_map = state.gantt_chart_planning_table.show_valid_lines.valid_lines_map;
    const show_line = valid_lines_map ? valid_lines_map.has(ownProps.line_uuid) : true;

    const is_filter_locked = state.gantt_chart_filters.is_filter_locked;

    // find first order without explanation since it marks
    let recommend_split_order_uuid = "";
    const explanations = state.gantt_chart_planning_table.recommend_plan.explanations;
    if (line_orders && explanations && explanations.length > 0) {
        for (const order of line_orders.orders) {
            if (recommend_split_order_uuid === "") {
                // we are still searching for first break
                if (
                    explanations.findIndex(
                        e =>
                            e.operation_uuid === order.uuid &&
                            (e.type === PLANNING_EXPLANATION_TYPE.frozen ||
                                e.type === PLANNING_EXPLANATION_TYPE.scheduled)
                    ) === -1
                ) {
                    recommend_split_order_uuid = order.uuid;
                }
            } else {
                // we found first break, check if we have another explanation
                // as that means we did some significant changes since last planning
                if (explanations.findIndex(e => e.operation_uuid === order.uuid) !== -1) {
                    recommend_split_order_uuid = "";
                    break;
                }
            }
        }
    }

    const valid_orders_map = state.gantt_chart_planning_table.show_valid_lines.valid_orders_map;
    const filtered_order_uuids: Set<string> = new Set(
        line_orders?.orders
            .filter(order => {
                const valid_order = valid_orders_map?.get(order.uuid);
                if (valid_order == null) {
                    return true;
                }

                return !valid_order.is_translucent;
            })
            .map(order => order.uuid)
    );

    return {
        line_orders,
        show_line,
        is_filter_locked,
        recommend_split_order_uuid,
        filtered_order_uuids,
    };
};
const lineOrderListMapDispatch = (dispatch: Dispatch<LineOrderListActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof lineOrderListMapState> & ReturnType<typeof lineOrderListMapDispatch>;
export default connect<
    ReturnType<typeof lineOrderListMapState>,
    ReturnType<typeof lineOrderListMapDispatch>,
    LineOrderListOwnProps,
    ReduxState
>(
    lineOrderListMapState,
    lineOrderListMapDispatch
)(LineOrderList);
